import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { format } from 'date-fns'
import { PricingEngine } from './utils/pricingEngine'
import { loadPriceOverrides, savePriceOverrides } from './utils/localStorage'
import PriceConfigPanel from './components/PriceConfigPanel'
import DateList from './components/DateList'
import DayDetails from './components/DayDetails'
import {
  PricingConfiguration,
  PriceConfig,
  BusinessModel,
  OTAChannel,
  LOSCategory,
  PAXCategory,
  PricingRule,
  DayCalculation,
  DayPriceOverride,
} from './types'
import { DollarSign, TrendingUp, AlertTriangle, X } from 'lucide-react'
import PricingMatrix from './components/PricingMatrix'
import MaxPriceTooltip from './components/MaxPriceTooltip'

const defaultPriceConfig: PriceConfig = {
  basePrice: 120,
  minPrice: 80,
  maxPrice: 200,
}

const defaultBusinessModel: BusinessModel = {
  type: 'direct',
  model: 'absolute_same',
}

const defaultOTAChannels: OTAChannel[] = [
  { id: 'direct', name: 'Direct', marginPercent: 0, enabled: true },
  { id: 'booking', name: 'Booking.com', marginPercent: 15, enabled: true },
  { id: 'airbnb', name: 'Airbnb', marginPercent: 12, enabled: true },
  { id: 'expedia', name: 'Expedia', marginPercent: 18, enabled: false },
]

const defaultLOSCategories: LOSCategory[] = [
  {
    id: 'short',
    name: 'Short Stay',
    minNights: 1,
    maxNights: 3,
    inputPriceDifference: 0,
    inputPriceDifferenceType: 'absolute',
    sellingPriceDifference: 10,
    sellingPriceDifferenceType: 'percent',
    priceModifier: 0,
    modifierType: 'percent',
  },
  {
    id: 'standard',
    name: 'Standard',
    minNights: 4,
    maxNights: 7,
    inputPriceDifference: 0,
    inputPriceDifferenceType: 'absolute',
    sellingPriceDifference: 0,
    sellingPriceDifferenceType: 'percent',
    priceModifier: 0,
    modifierType: 'percent',
    isDefault: true,
  },
  {
    id: 'weekly',
    name: 'Weekly',
    minNights: 8,
    maxNights: 14,
    inputPriceDifference: 0,
    inputPriceDifferenceType: 'absolute',
    sellingPriceDifference: -5,
    sellingPriceDifferenceType: 'percent',
    priceModifier: 0,
    modifierType: 'percent',
  },
  {
    id: 'long',
    name: 'Long Stay',
    minNights: 15,
    inputPriceDifference: 0,
    inputPriceDifferenceType: 'absolute',
    sellingPriceDifference: -15,
    sellingPriceDifferenceType: 'percent',
    priceModifier: 0,
    modifierType: 'percent',
  },
]

const defaultPAXCategories: PAXCategory[] = [
  {
    id: 'single',
    name: 'Single',
    persons: 1,
    sellingPriceDifference: -20,
    sellingPriceDifferenceType: 'percent',
  },
  {
    id: 'standard',
    name: 'Standard',
    persons: 2,
    sellingPriceDifference: 0,
    sellingPriceDifferenceType: 'percent',
    isDefault: true,
  },
  {
    id: 'family',
    name: 'Family',
    persons: 4,
    sellingPriceDifference: 15,
    sellingPriceDifferenceType: 'percent',
  },
  {
    id: 'group',
    name: 'Group',
    persons: 6,
    sellingPriceDifference: 25,
    sellingPriceDifferenceType: 'percent',
  },
]

const defaultPricingRules: PricingRule[] = [
  {
    id: 'weekend_fri_sat',
    name: 'Weekend Premium (Fri-Sat)',
    description: 'Higher prices for Friday and Saturday nights',
    dayOfWeekFilter: [5, 6], // Friday, Saturday
    priceModifier: 20,
    enabled: true,
  },
  {
    id: 'sunday',
    name: 'Sunday',
    description: 'Special pricing for Sunday nights',
    dayOfWeekFilter: [0], // Sunday
    priceModifier: 10,
    enabled: true,
  },
  {
    id: 'summer',
    name: 'Summer Season',
    description: 'Peak season pricing for summer months',
    monthFilter: [6, 7, 8], // June, July, August
    priceModifier: 30,
    enabled: true,
  },
  {
    id: 'christmas',
    name: 'Christmas Holiday',
    description: 'Premium pricing for Christmas holiday period',
    dateRangeFilter: {
      start: new Date(2024, 11, 20), // December 20, 2024
      end: new Date(2024, 11, 26), // December 26, 2024
    },
    priceModifier: 40,
    enabled: true,
  },
  {
    id: 'new_years',
    name: "New Year's Holiday",
    description: "Premium pricing for New Year's Eve and Day",
    dateRangeFilter: {
      start: new Date(2024, 11, 30), // December 30, 2024
      end: new Date(2025, 0, 2), // January 2, 2025
    },
    priceModifier: 50,
    enabled: true,
  },
]

function App() {
  const [priceConfig, setPriceConfig] = useState<PriceConfig>(
    defaultPriceConfig,
  )
  const [businessModel, setBusinessModel] = useState<BusinessModel>(
    defaultBusinessModel,
  )
  const [otaChannels, setOtaChannels] = useState<OTAChannel[]>(
    defaultOTAChannels,
  )
  const [losCategories, setLOSCategories] = useState<LOSCategory[]>(
    defaultLOSCategories,
  )
  const [paxCategories, setPAXCategories] = useState<PAXCategory[]>(
    defaultPAXCategories,
  )
  const [pricingRules, setPricingRules] = useState<PricingRule[]>(
    defaultPricingRules,
  )
  const [priceOverrides, setPriceOverrides] = useState<DayPriceOverride[]>([])

  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [selectedChannel, setSelectedChannel] = useState<string>('direct')
  const [scrollToDate, setScrollToDate] = useState<Date>(new Date()) // Default to today
  const [startMonth, setStartMonth] = useState<Date>(() => {
    const now = new Date()
    return new Date(now.getFullYear(), 0, 1) // January 1st of current year
  })
  const [monthsToShow] = useState<number>(12) // Show 12 months in the list

  const [calculations, setCalculations] = useState<DayCalculation[]>([])
  const [isCalculating, setIsCalculating] = useState(false)

  // Load price overrides from localStorage on component mount
  useEffect(() => {
    const savedOverrides = loadPriceOverrides()
    setPriceOverrides(savedOverrides)
  }, [])

  // Save price overrides to localStorage whenever they change
  useEffect(() => {
    savePriceOverrides(priceOverrides)
  }, [priceOverrides])

  // Create pricing configuration
  const pricingConfiguration: PricingConfiguration = useMemo(
    () => ({
      priceConfig,
      businessModel,
      otaChannels,
      losCategories,
      paxCategories,
      pricingRules,
      priceOverrides,
    }),
    [
      priceConfig,
      businessModel,
      otaChannels,
      losCategories,
      paxCategories,
      pricingRules,
      priceOverrides,
    ],
  )

  // Create pricing engine
  const pricingEngine = useMemo(() => new PricingEngine(pricingConfiguration), [
    pricingConfiguration,
  ])

  // Calculate suggested max price
  const suggestedMaxPrice = useMemo(() => pricingEngine.suggestMaxPrice(), [
    pricingEngine,
  ])

  // Debounced price calculation
  const calculatePrices = useCallback(async () => {
    if (isCalculating) return

    setIsCalculating(true)
    try {
      const allCalculations: DayCalculation[] = []
      for (let i = 0; i < monthsToShow; i++) {
        const monthDate = new Date(
          startMonth.getFullYear(),
          startMonth.getMonth() + i,
        )
        const year = monthDate.getFullYear()
        const month = monthDate.getMonth() + 1
        const monthlyCalculations = pricingEngine.calculateMonthlyPrices(
          year,
          month,
        )
        allCalculations.push(...monthlyCalculations)

        // Allow UI to update between months
        await new Promise((resolve) => setTimeout(resolve, 0))
      }
      setCalculations(allCalculations)
    } finally {
      setIsCalculating(false)
    }
  }, [pricingEngine, startMonth, monthsToShow, isCalculating])

  // Calculate prices when configuration changes
  useEffect(() => {
    const timeoutId = setTimeout(calculatePrices, 300) // Debounce calculations
    return () => clearTimeout(timeoutId)
  }, [calculatePrices])

  // Update selected channel if it becomes disabled
  useEffect(() => {
    const enabledChannels = otaChannels.filter((channel) => channel.enabled)
    if (
      enabledChannels.length > 0 &&
      !enabledChannels.find((ch) => ch.id === selectedChannel)
    ) {
      setSelectedChannel(enabledChannels[0].id)
    }
  }, [otaChannels, selectedChannel])

  const handlePriceOverride = (
    date: string,
    price: number,
    losId?: string,
    paxId?: string,
  ) => {
    setPriceOverrides((prev) => {
      if (price === -1) {
        // Remove override (special case)
        if (losId && paxId) {
          return prev.filter(
            (override) =>
              !(
                override.date === date &&
                override.losId === losId &&
                override.paxId === paxId
              ),
          )
        }
        return prev.filter((override) => override.date !== date)
      }

      if (losId && paxId) {
        // Handle cell-specific override
        const existingIndex = prev.findIndex(
          (override) =>
            override.date === date &&
            override.losId === losId &&
            override.paxId === paxId,
        )

        if (existingIndex >= 0) {
          // Update existing override
          const updated = [...prev]
          updated[existingIndex] = { date, basePrice: price, losId, paxId }
          return updated
        } else {
          // Add new override
          return [...prev, { date, basePrice: price, losId, paxId }]
        }
      } else {
        // Handle day-level override (backward compatibility)
        const existingIndex = prev.findIndex(
          (override) =>
            override.date === date && !override.losId && !override.paxId,
        )

        if (existingIndex >= 0) {
          // Update existing override
          const updated = [...prev]
          updated[existingIndex] = { date, basePrice: price }
          return updated
        } else {
          // Add new override
          return [...prev, { date, basePrice: price }]
        }
      }
    })
  }

  const handleClearOverrides = () => {
    setPriceOverrides([])
  }

  const handlePricingRuleToggle = (ruleId: string) => {
    setPricingRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule,
      ),
    )
  }

  return (
    <div className="h-screen bg-gray-50 flex">
      <div className="w-96 flex-shrink-0">
        <PriceConfigPanel
          priceConfig={priceConfig}
          businessModel={businessModel}
          otaChannels={otaChannels}
          pricingRules={pricingRules}
          losCategories={losCategories}
          paxCategories={paxCategories}
          onPriceConfigChange={setPriceConfig}
          onBusinessModelChange={setBusinessModel}
          onChannelChange={setOtaChannels}
          onPricingRuleToggle={handlePricingRuleToggle}
          onPricingRuleChange={setPricingRules}
          onLOSCategoryChange={setLOSCategories}
          onPAXCategoryChange={setPAXCategories}
          onClearOverrides={handleClearOverrides}
          overrideCount={priceOverrides.length}
          suggestedMaxPrice={suggestedMaxPrice.regular}
        />
      </div>

      <div className="flex-1 flex h-full relative">
        <div className="flex-1 h-full">
          <DateList
            calculations={calculations}
            selectedDate={selectedDate}
            otaChannels={otaChannels}
            losCategories={losCategories}
            paxCategories={paxCategories}
            priceOverrides={priceOverrides}
            startMonth={startMonth}
            monthsToShow={monthsToShow}
            onDateSelect={setSelectedDate}
            basePrice={priceConfig.basePrice}
            onPriceOverride={handlePriceOverride}
            businessModel={businessModel}
            scrollToDate={scrollToDate}
          />
        </div>

        <div className="w-96 flex-shrink-0 border-l border-gray-200 bg-white">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Price Points
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Price (€)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={priceConfig.basePrice}
                    onChange={(e) =>
                      setPriceConfig({
                        ...priceConfig,
                        basePrice: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="pricing-input w-full pl-8 text-right"
                    placeholder="Average yearly price"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    €
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Average price throughout the year
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Price (€){' '}
                  <span className="text-gray-400">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={priceConfig.minPrice || ''}
                    onChange={(e) =>
                      setPriceConfig({
                        ...priceConfig,
                        minPrice: parseFloat(e.target.value) || undefined,
                      })
                    }
                    className="pricing-input w-full pl-8 text-right"
                    placeholder="Minimum acceptable price"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    €
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Never sell below this price
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Price (€)
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1 group">
                    <input
                      type="number"
                      value={priceConfig.maxPrice}
                      onChange={(e) =>
                        setPriceConfig({
                          ...priceConfig,
                          maxPrice: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="pricing-input w-full pl-8 text-right"
                      placeholder="Legal maximum price"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      €
                    </span>
                    <div className="absolute right-0 top-full mt-2 w-[520px] bg-white rounded-lg shadow-lg p-4 z-[9999] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-200">
                      <MaxPriceTooltip
                        priceConfig={priceConfig}
                        losCategories={losCategories}
                        paxCategories={paxCategories}
                        otaChannels={otaChannels}
                        pricingRules={pricingRules}
                        suggestedMaxPrice={suggestedMaxPrice}
                      />
                    </div>
                  </div>
                  {suggestedMaxPrice &&
                    suggestedMaxPrice.regular !== priceConfig.maxPrice && (
                      <button
                        onClick={() =>
                          setPriceConfig({
                            ...priceConfig,
                            maxPrice: suggestedMaxPrice.regular,
                          })
                        }
                        className="px-3 py-2 text-xs bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                        title={`Use suggested: €${suggestedMaxPrice.regular}`}
                      >
                        Use €{suggestedMaxPrice.regular}
                      </button>
                    )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Legal maximum for price list
                </p>
              </div>

              {/* Buffer and Max Price Suggestion */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Buffer (%)
                </label>
                <input
                  type="number"
                  value={priceConfig.bufferPercent ?? 20}
                  onChange={(e) =>
                    setPriceConfig({
                      ...priceConfig,
                      bufferPercent: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="pricing-input w-full text-sm"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Price Overrides List */}
            <div className="mt-8 border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  Price Overrides
                </h3>
                {priceOverrides.length > 0 && (
                  <button
                    onClick={handleClearOverrides}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  >
                    Clear All
                  </button>
                )}
              </div>
              {priceOverrides.length === 0 ? (
                <p className="text-sm text-gray-500">No price overrides set</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {priceOverrides.map((override) => {
                    const date = new Date(override.date)
                    return (
                      <div
                        key={override.date}
                        className="flex items-center justify-between p-2 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
                        onClick={() => {
                          setSelectedDate(date)
                          setScrollToDate(date)
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {format(date, 'MMM d, yyyy')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {format(date, 'EEEE')}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            €{override.basePrice}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handlePriceOverride(override.date, -1)
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Remove override"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedDate && (
          <div className="fixed bottom-0 left-96 right-0 z-20 border-t border-gray-200 bg-white shadow-xl max-h-[60vh] overflow-y-auto">
            <DayDetails
              selectedDate={selectedDate}
              calculation={
                selectedDate
                  ? calculations.find(
                      (calc) =>
                        calc.date === format(selectedDate, 'yyyy-MM-dd'),
                    )
                  : undefined
              }
              selectedChannel={selectedChannel}
              otaChannels={otaChannels}
              losCategories={losCategories}
              paxCategories={paxCategories}
              priceOverrides={priceOverrides}
              onPriceOverride={handlePriceOverride}
              onChannelSelect={setSelectedChannel}
              onClose={() => setSelectedDate(undefined)}
              basePrice={priceConfig.basePrice}
              businessModel={businessModel}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default App
